import brokersJson from "./data/brokers.json";
import propertiesJson from "./data/properties.json";
import motorsJson from "./data/motors.json";
import policiesJson from "./data/policies.json";
import propertyExposuresJson from "./data/property_policy_exposures.json";
import motorExposuresJson from "./data/motor_policy_exposures.json";
import propertyClaimsJson from "./data/property_claims.json";
import motorClaimsJson from "./data/motor_claims.json";

export const brokers = brokersJson;
export const properties = propertiesJson;
export const motors = motorsJson;
export const policies = policiesJson;
export const propertyExposures = propertyExposuresJson;
export const motorExposures = motorExposuresJson;
export const propertyClaims = propertyClaimsJson;
export const motorClaims = motorClaimsJson;

const byId = (rows) => Object.fromEntries(rows.map((row) => [row.id, row]));

const groupBy = (rows, key) => {
  const groups = {};
  rows.forEach((row) => {
    const groupKey = row[key];
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(row);
  });
  return groups;
};

export const brokersById = byId(brokers);
export const propertiesById = byId(properties);
export const motorsById = byId(motors);
export const policiesById = byId(policies);

export const propertiesByBroker = groupBy(properties, "broker_id");
export const motorsByBroker = groupBy(motors, "broker_id");
export const policiesByBroker = groupBy(policies, "broker_id");
export const exposuresByProperty = groupBy(propertyExposures, "property_id");
export const exposuresByMotor = groupBy(motorExposures, "motor_id");
export const claimsByProperty = groupBy(propertyClaims, "property_id");
export const claimsByMotor = groupBy(motorClaims, "motor_id");

export const isActiveExposure = (exposure) =>
  !exposure.cancelled_at && policiesById[exposure.policy_id]?.status === "active";

const sumActive = (exposures, field) =>
  (exposures || [])
    .filter(isActiveExposure)
    .reduce((total, exposure) => total + (exposure[field] || 0), 0);

export const brokerStatsById = Object.fromEntries(
  brokers.map((broker) => {
    const brokerProperties = propertiesByBroker[broker.id] || [];
    const brokerMotors = motorsByBroker[broker.id] || [];
    const propertyInsured = brokerProperties.reduce(
      (total, property) =>
        total + sumActive(exposuresByProperty[property.id], "building_sum_insured"),
      0,
    );
    const contentsInsured = brokerProperties.reduce(
      (total, property) =>
        total + sumActive(exposuresByProperty[property.id], "contents_sum_insured"),
      0,
    );
    const motorInsured = brokerMotors.reduce(
      (total, motor) =>
        total + sumActive(exposuresByMotor[motor.id], "vehicle_sum_insured"),
      0,
    );
    const brokerPropertyClaims = brokerProperties.flatMap(
      (property) => claimsByProperty[property.id] || [],
    );
    const brokerMotorClaims = brokerMotors.flatMap(
      (motor) => claimsByMotor[motor.id] || [],
    );
    const brokerClaims = [...brokerPropertyClaims, ...brokerMotorClaims];

    return [
      broker.id,
      {
        propertyCount: brokerProperties.length,
        motorCount: brokerMotors.length,
        combinedCount: brokerProperties.length + brokerMotors.length,
        propertyInsured,
        contentsInsured,
        motorInsured,
        totalInsured: propertyInsured + contentsInsured + motorInsured,
        totalPremium:
          brokerProperties.reduce((total, property) => total + (property.premium || 0), 0)
          + brokerMotors.reduce((total, motor) => total + (motor.premium || 0), 0),
        propertyClaimCount: brokerPropertyClaims.length,
        motorClaimCount: brokerMotorClaims.length,
        claimCount: brokerClaims.length,
        claimedAmount: brokerClaims.reduce((total, claim) => total + claim.claimed_amount, 0),
        paidAmount: brokerClaims.reduce((total, claim) => total + claim.paid_amount, 0),
      },
    ];
  }),
);

export const entitiesForLayer = {
  broker: brokers,
  property: properties,
  motor: motors,
};
